import { ReactQueryKeys } from '@/utils/react-query-keys';
import { useQuery } from '@tanstack/react-query';
import {
  getRecordingUrl,
  RecordingCategory,
} from '@/services/conversations-service/conversations-service';
import { RecordingStatus } from '@/services/conversations-service/conversations-service.types';

interface UseRecordingUrlProps {
  conversationId: number | undefined;
  category?: RecordingCategory;
  enabled?: boolean;
}

// While the recording is still processing, refetch every 15s so the player
// surfaces automatically when Twilio's webhook lands. Stop polling once the
// status is final ('ready' or 'unavailable') — there's nothing left to wait
// for and continued polling would be wasted requests.
const PROCESSING_REFETCH_INTERVAL_MS = 15_000;

export const useRecordingUrl = ({
  conversationId,
  category,
  enabled = true,
}: UseRecordingUrlProps) => {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ReactQueryKeys.RECORDING_URL(conversationId, category),
    queryFn: () => getRecordingUrl(conversationId!, category),
    enabled: enabled && conversationId !== undefined,
    // 30s is short enough that reopening the drawer while a recording is
    // still processing triggers a fresh fetch (instead of showing stale
    // 'processing' from cache for up to 5 minutes — which was the original
    // value, fine for a binary 200/404 response but wrong for our new
    // transient state). Long enough that closing/reopening within the same
    // glance doesn't slam the API.
    staleTime: 30 * 1000,
    retry: false,
    // React-Query supports a function form to compute interval per cycle.
    refetchInterval: (query) =>
      query.state.data?.status === 'processing' ? PROCESSING_REFETCH_INTERVAL_MS : false,
  });

  const status: RecordingStatus | undefined = data?.status;

  return {
    recordingUrl: data?.url ?? undefined,
    recordingStatus: status,
    isLoadingRecordingUrl: isPending,
    isErrorRecordingUrl: isError,
    refetchRecordingUrl: refetch,
  };
};
